import React, { useState, useEffect, useMemo } from "react";
import FitNumber from "../../../common/FitNumber";
import { fetchData } from "../../../../utils/functions";
import ReactApexChart from "react-apexcharts";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import NoData, { hasData } from "../../../common/NoData";
import SharedDonut from "../../../common/DonutChart";
import DashboardBarChart from "../../../common/DashboardBarChart";
import {
  BRAND,
  NEUTRAL,
  GENDER_COLORS,
  STATUS_COLORS,
  getPaletteColor,
} from "../../../../utils/chartTheme";
import { formatCount, formatCountFit } from "../../../../utils/numberFormat";


const formatCompactNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) return "0";
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  return num.toString();
};

const CHART_SLATE = getPaletteColor(2);

// ActivityTypeCard
const ActivityTypeCard = ({ title, count, variant = "default" }) => {
  const borderColors = {
    default: "#ededed",
    approved: BRAND.teal,
    rejected: CHART_SLATE,
    pending: STATUS_COLORS.planned,
  };
  const borderColor = borderColors[variant] || borderColors.default;
  return (
    <div>
      <div
        className="bg-[#f7f7f7] text-white h-28 p-2.5 rounded-3xl border-2 hover:border-teal-300 transition-all duration-300 hover:shadow-md"
        style={{ borderColor }}
      >
        <h3 className="text-2xl text-center mt-4 mb-0 text-[#3A3A3A] p-0 font-bold text-balance">
          <FitNumber value={count ?? 0} text={formatCount(count ?? 0)} />
        </h3>
        <h6 className="text-xs text-center p-0 m-0 text-[#787878] font-semibold">
          {title}
        </h6>
      </div>
    </div>
  );
};

// Households Served Donut
const HouseholdsServedDonut = ({ maleTotal, femaleTotal, totalCount }) => (
  <SharedDonut
    items={[
      { name: "Male", value: maleTotal, color: GENDER_COLORS.male },
      { name: "Female", value: femaleTotal, color: GENDER_COLORS.female },
    ]}
    centerValue={totalCount}
  />
);

// Head of Household Circles
const HeadOfHouseholdCircles = ({ maleHead, femaleHead, totalHeads }) => {
  const chartData = [
    { label: "Total Heads", count: totalHeads, color: BRAND.teal },
    { label: "Male Heads", count: maleHead, color: GENDER_COLORS.male },
    { label: "Female Heads", count: femaleHead, color: GENDER_COLORS.female },
  ];
  return (
    <div className="flex justify-center items-center">
      <div className="grid grid-cols-3 gap-5 w-full max-w-md">
        {chartData.map((item, index) => {
          const percentage =
            totalHeads > 0
              ? index === 0
                ? 100
                : (item.count / totalHeads) * 100
              : 0;
          const radius = 38;
          const circumference = 2 * Math.PI * radius;
          const strokeDashoffset =
            circumference - (percentage / 100) * circumference;
          return (
            <div key={index} className="flex flex-col items-center">
              <div className="relative w-28 h-28">
                <svg className="transform -rotate-90 w-28 h-28">
                  <circle
                    cx="56"
                    cy="56"
                    r={radius}
                    stroke={NEUTRAL.border}
                    strokeWidth="9"
                    fill="transparent"
                  />
                  <circle
                    cx="56"
                    cy="56"
                    r={radius}
                    stroke={item.color}
                    strokeWidth="9"
                    fill="transparent"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    className="transition-all duration-500"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span
                    className="text-lg font-bold whitespace-nowrap"
                    style={{ color: item.color }}
                    title={Number(item.count || 0).toLocaleString()}
                  >
                    {formatCompactNumber(item.count)}
                  </span>
                </div>
              </div>
              <div className="mt-2.5 text-center">
                <p className="text-xs font-semibold text-gray-700 leading-normal">
                  {item.label}
                </p>
                <p className="text-xs text-gray-500 leading-normal mt-0.5">
                  {percentage.toFixed(1)}%
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Last 7 Days District Wise Chart
const Last7DaysDistrictWiseChart = ({ last7DaysData }) => {
  const { categories = [], data = [] } = last7DaysData || {};
  const chartOptions = {
    chart: {
      type: "area",
      toolbar: { show: false },
      height: 280,
      zoom: { enabled: false },
      animations: { enabled: true, easing: "easeinout", speed: 800 },
    },
    dataLabels: {
      enabled: true,
      offsetY: -8,
  
      style: { fontSize: "10px", fontWeight: 700, colors: ["#ffffff"] },
      background: {
        enabled: true,
        foreColor: "#374151",
        borderRadius: 3,
        padding: 4,
        opacity: 0.95,
        borderWidth: 1,
        borderColor: "#e5e7eb",
      },
    },
    stroke: { curve: "smooth", width: 3, colors: [BRAND.teal] },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.2,
        stops: [0, 90, 100],
        colorStops: [
          { offset: 0, color: BRAND.teal, opacity: 0.6 },
          { offset: 100, color: BRAND.tealLight, opacity: 0.1 },
        ],
      },
    },
    xaxis: {
      categories,
      labels: {
        rotate: -45,
        style: { fontSize: "11px", fontWeight: 600, colors: NEUTRAL.muted },
      },
    },
    yaxis: {
      title: {
        text: "New Households",
        style: { fontSize: "12px", fontWeight: 300 },
      },
      labels: { formatter: (val) => Math.round(val) },
    },
    grid: { borderColor: NEUTRAL.grid, strokeDashArray: 4 },
    tooltip: {
      y: { formatter: (val) => `${val.toLocaleString()} households` },
    },
    markers: {
      size: 5,
      colors: [BRAND.teal],
      strokeColors: "#fff",
      strokeWidth: 2,
    },
  };

  const chartSeries = [{ name: "New Households", data }];

  if (!categories.length || !hasData(data)) {
    return <NoData className="h-full" />;
  }

  const totalAdded = data.reduce((sum, val) => sum + val, 0);
  const averagePerDay = (totalAdded / data.length).toFixed(1);

  return (
    <div className="w-full h-full flex flex-col">
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-[#E6F2F2] rounded-lg p-3 border border-[#7FBFC1]">
          <p className="text-xs text-[#0B5557] font-medium">Total Survey</p>
          <p className="text-2xl font-bold text-[#137E81]">{totalAdded}</p>
        </div>
        <div className="bg-[#E6F2F2] rounded-lg p-3 border border-[#7FBFC1]">
          <p className="text-xs text-[#0B5557] font-medium">Avg/Day</p>
          <p className="text-2xl font-bold text-[#137E81]">{averagePerDay}</p>
        </div>
      </div>
      <ReactApexChart
        options={chartOptions}
        series={chartSeries}
        type="area"
        height={200}
      />
    </div>
  );
};

// Family Size Distribution (horizontal bars)
const FAMILY_SIZE_RANGES = {
  Small: "1-3",
  Medium: "4-5",
  Large: "6-7",
  "Very Large": "8-9",
  "Extra Large": "10+",
};

const FamilySizePie = ({ familySizeData }) => {
  const items = (familySizeData || []).map((item) => {
    const label = item.group || "Unknown";
    const range = FAMILY_SIZE_RANGES[label];
    return {
      name: range ? `${label} (${range})` : label,
      count: item.count || 0,
    };
  });
  return <DashboardBarChart horizontal items={items} unit="households" />;
};

const InformationMembersTab = ({ filters }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [data, setData] = useState({
    interviewed: 0,
    registeredComplete: 0,
    registeredIncomplete: 0,
    completionPercentage: 0,
    incompletePercentage: 0,
    registeredSPHF: 0,
    registeredNonSPHF: 0,
    sphfPercentage: "0",
    nonSphfPercentage: "0",
    maleHead: 0,
    femaleHead: 0,
    totalHeads: 0,
    familyStats: { mean: 0, min: 0, max: 0, median: 0, std: 0 },
    everMarriedByAge: {
      "18+": { male: 0, female: 0 },
      "14-17": { male: 0, female: 0 },
      "11-13": { male: 0, female: 0 },
    },
    householdCounts: { approvedCount: 0, rejectCount: 0, listingCount: 0 },
    sphfStats: {
      sphf_beneficiary: 0,
      non_sphf: 0,
      total: 0,
      construction_status: {
        "Not started": 0,
        Ongoing: 0,
        Paused: 0,
        Completed: 0,
      },
    },
  });

  const [householdMembersData, setHouseholdMembersData] = useState({
    total: 0,
    maleTotal: 0,
    femaleTotal: 0,
    otherTotal: 0,
  });

  const [familySizeData, setFamilySizeData] = useState([]);
  const [districtWiseApprovedHousehold, setDistrictWiseApprovedHousehold] =
    useState(null);
  const [last7DaysData, setLast7DaysData] = useState({
    categories: [],
    data: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [
          interviewRes,
          headRes,
          familyRes,
          maritalRes,
          countsRes,
          sphfRes,
          membersRes,
          familySizeRes,
          completeHouseholdsRes,
          districtWiseRes,
          last7DaysRes,
        ] = await Promise.all([
          fetchData(
            "Household-Interview-Status",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/Household-Interview-Status",
            dispatch
          ),
          fetchData(
            "household-head-gender-stats",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/household-head-gender-stats",
            dispatch
          ),
          fetchData(
            "household-family-size-stats",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/household-family-size-stats",
            dispatch
          ),
          fetchData(
            "household-ever-married-by-age",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/household-ever-married-by-age",
            dispatch
          ),
          fetchData(
            "household-approved-reject-count",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/household-approved-reject-count",
            dispatch
          ),
          fetchData(
            "sphf-beneficiary-stats",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/sphf-beneficiary-stats",
            dispatch
          ),
          fetchData(
            "household-members-count",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/household-members-count",
            dispatch
          ),
          fetchData(
            "household-age-group-distribution",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/household-age-group-distribution",
            dispatch
          ),
          fetchData(
            "complete-households-count",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/complete-households-count",
            dispatch
          ),
          fetchData(
            "District-Wise-Approved-Households",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/District-Wise-Approved-Households",
            dispatch
          ),
          fetchData(
            "household-last-7-days-district-wise",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/household-last-7-days-district-wise",
            dispatch
          ),
        ]);

        const sphfTotal =
          (sphfRes.data?.sphf_beneficiary || 0) + (sphfRes.data?.non_sphf || 0);
        const sphfPct =
          sphfTotal > 0
            ? ((sphfRes.data?.sphf_beneficiary || 0) / sphfTotal) * 100
            : 0;
        const nonSphfPct =
          sphfTotal > 0 ? ((sphfRes.data?.non_sphf || 0) / sphfTotal) * 100 : 0;

        setData({
          interviewed: interviewRes.data?.completed_interviews || 0,
          registeredComplete: completeHouseholdsRes.data?.completeCount || 0,
          registeredIncomplete:
            completeHouseholdsRes.data?.incompleteCount || 0,
          completionPercentage:
            completeHouseholdsRes.data?.completionPercentage || 0,
          incompletePercentage:
            completeHouseholdsRes.data?.incompletePercentage || 0,
          registeredSPHF: sphfRes.data?.sphf_beneficiary || 0,
          registeredNonSPHF: sphfRes.data?.non_sphf || 0,
          sphfPercentage: sphfPct.toFixed(1),
          nonSphfPercentage: nonSphfPct.toFixed(1),
          maleHead: headRes.data?.male || 0,
          femaleHead: headRes.data?.female || 0,
          totalHeads: (headRes.data?.male || 0) + (headRes.data?.female || 0),
          familyStats: familyRes.data || {
            mean: 0,
            min: 0,
            max: 0,
            median: 0,
            std: 0,
          },
          everMarriedByAge: maritalRes.data || {
            "18+": { male: 0, female: 0 },
            "14-17": { male: 0, female: 0 },
            "11-13": { male: 0, female: 0 },
          },
          householdCounts: countsRes.data || {
            approvedCount: 0,
            rejectCount: 0,
            listingCount: 0,
          },
          sphfStats: sphfRes.data || {
            sphf_beneficiary: 0,
            non_sphf: 0,
            total: 0,
            construction_status: {
              "Not started": 0,
              Ongoing: 0,
              Paused: 0,
              Completed: 0,
            },
          },
        });

        setHouseholdMembersData(
          membersRes?.data || {
            total: 0,
            maleTotal: 0,
            femaleTotal: 0,
            otherTotal: 0,
          }
        );

        const familySizeGroupData = familySizeRes?.data?.familySizeGroup || [];
        setFamilySizeData(familySizeGroupData);

        setDistrictWiseApprovedHousehold(districtWiseRes?.data || null);

        setLast7DaysData(last7DaysRes?.data || { categories: [], data: [] });
      } catch (err) {
        console.error("Error loading Information & Members tab:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [filters, navigate, dispatch]);

  const totalSurveyed = data.householdCounts.listingCount || 0;
  const approvedCount = data.householdCounts.approvedCount || 0;
  const rejectCount = data.householdCounts.rejectCount || 0;
  const pendingCount = totalSurveyed - approvedCount - rejectCount;

  const maritalCategories = ["Age 18 & Above", "Age 14–17", "Age 11–13"];

  const maritalSeries = [
    {
      name: "Male",
      data: Object.values(data.everMarriedByAge).map((d) => d.male || 0),
    },
    {
      name: "Female",
      data: Object.values(data.everMarriedByAge).map((d) => d.female || 0),
    },
  ];

  const grandTotals = useMemo(() => {
    if (
      !districtWiseApprovedHousehold ||
      districtWiseApprovedHousehold.length === 0
    )
      return null;
    const totalApproved = districtWiseApprovedHousehold.reduce(
      (sum, d) => sum + (parseInt(d.total_approved_households) || 0),
      0
    );
    const maleCount = districtWiseApprovedHousehold.reduce(
      (sum, d) => sum + (parseInt(d.male_count) || 0),
      0
    );
    const femaleCount = districtWiseApprovedHousehold.reduce(
      (sum, d) => sum + (parseInt(d.female_count) || 0),
      0
    );
    const totalMembers = districtWiseApprovedHousehold.reduce(
      (sum, d) => sum + (parseInt(d.total_members) || 0),
      0
    );
    const overallMalePct =
      totalMembers > 0 ? ((maleCount / totalMembers) * 100).toFixed(2) : 0;
    const overallFemalePct =
      totalMembers > 0 ? ((femaleCount / totalMembers) * 100).toFixed(2) : 0;
    return {
      totalApproved,
      maleCount,
      femaleCount,
      totalMembers,
      overallMalePct,
      overallFemalePct,
    };
  }, [districtWiseApprovedHousehold]);

  const has = {
    surveyed: hasData(totalSurveyed),
    members: hasData(householdMembersData.total),
    heads: hasData(data.totalHeads),
    keyStats: hasData(
      data.interviewed,
      data.registeredComplete,
      data.registeredIncomplete,
      data.registeredSPHF,
      data.registeredNonSPHF
    ),
    last7Days: hasData(last7DaysData.data),
    sphf: hasData(data.registeredSPHF, data.registeredNonSPHF),
    marital: hasData(data.everMarriedByAge),
    familySize: hasData(familySizeData.map((i) => i.count)),
    hhSize: hasData(data.familyStats),
    genderTable: hasData(
      (districtWiseApprovedHousehold || []).map((d) => d.total_members)
    ),
  };

  const anyData = Object.values(has).some(Boolean);
  const showRow = () => loading || anyData;
  const panel = (hasPanelData, body) =>
    !loading && !hasPanelData ? <NoData /> : body;

  return (
    <div className="space-y-4">
      {!loading && !Object.values(has).some(Boolean) && (
        <NoData className="py-16" />
      )}
      {/* Row 1: HH Surveyed, Household Members, Head of Household */}
      {showRow(has.surveyed, has.members, has.heads) && (
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-4">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Households Surveyed ({formatCount(totalSurveyed)})
          </h3>
          {panel(has.surveyed, <>
          <div className="grid grid-cols-3 gap-2 mt-3">
            <ActivityTypeCard
              title="Approved"
              count={approvedCount}
              variant="approved"
            />
            <ActivityTypeCard
              title="Pending"
              count={pendingCount}
              variant="pending"
            />
            <ActivityTypeCard
              title="Rejected"
              count={rejectCount}
              variant="rejected"
            />
          </div>
          <p className="text-sm text-center text-gray-600 mt-4">
            Approval Rate:{" "}
            {totalSurveyed > 0
              ? ((approvedCount / totalSurveyed) * 100).toFixed(1)
              : "0"}
            %
          </p>
          </>)}
        </div>
        <div className="lg:col-span-4">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Household Members
          </h3>
          {panel(has.members,
          <div className="mt-3">
            <HouseholdsServedDonut
              maleTotal={householdMembersData.maleTotal}
              femaleTotal={householdMembersData.femaleTotal}
              totalCount={householdMembersData.total}
            />
          </div>)}
        </div>
        <div className="lg:col-span-4">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Head of Household
          </h3>
          {panel(has.heads,
          <div className="mt-4">
            <HeadOfHouseholdCircles
              maleHead={data.maleHead}
              femaleHead={data.femaleHead}
              totalHeads={data.totalHeads}
            />
          </div>)}
        </div>
      </div>
      )}

      {/* Row 2: Key Statistics, Household (Last 7 Days), SPHF vs Non-SPHF */}
      {showRow(has.keyStats, has.last7Days, has.sphf) && (<>
      <hr className="border-t-2 border-dashed border-teal-700 first:hidden" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Key Statistics Table */}
        <div className="bg-white flex flex-col h-full">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase mb-3 text-balance">
            Key Statistics (Overall)
          </h3>
          {panel(has.keyStats,
          <div className="flex-1 flex items-center">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-teal-700 text-white">
                  <th className="p-4 text-left rounded-tl-xl">Indicator</th>
                  <th className="p-4 text-center">Count</th>
                  <th className="p-4 text-center rounded-tr-xl">%</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                <tr className="border-b hover:bg-gray-50 transition">
                  <td className="p-4">Households Interviewed</td>
                  <td className="text-center font-bold">
                    {formatCount(data.interviewed)}
                  </td>
                  <td className="text-center">-</td>
                </tr>
                <tr className="border-b bg-gray-50 hover:bg-gray-100">
                  <td className="p-4">Registered (Complete)</td>
                  <td className="text-center font-bold text-gray-800">
                    {formatCount(data.registeredComplete)}
                  </td>
                  <td className="text-center font-bold text-gray-800">
                    {data.completionPercentage}%
                  </td>
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-4">Registered (Incomplete)</td>
                  <td className="text-center font-bold text-gray-800">
                    {formatCount(data.registeredIncomplete)}
                  </td>
                  <td className="text-center font-bold text-gray-800">
                    {data.incompletePercentage}%
                  </td>
                </tr>
                <tr className="border-b bg-gray-50 hover:bg-gray-100">
                  <td className="p-4">Registered (SPHF)</td>
                  <td className="text-center font-bold text-gray-800">
                    {formatCount(data.registeredSPHF)}
                  </td>
                  <td className="text-center font-bold text-gray-800">
                    {data.sphfPercentage}%
                  </td>
                </tr>
                <tr className="border-b hover:bg-gray-50">
                  <td className="p-4">Registered (Non-SPHF)</td>
                  <td className="text-center font-bold text-gray-800">
                    {formatCount(data.registeredNonSPHF)}
                  </td>
                  <td className="text-center font-bold text-gray-800">
                    {data.nonSphfPercentage}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>)}
        </div>

        {/* Household (Last 7 Days) */}
        <div className="bg-white flex flex-col h-full rounded-lg shadow-sm">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Household (Last 7 Days)
          </h3>
          <div className="flex-1 p-4">
            <Last7DaysDistrictWiseChart last7DaysData={last7DaysData} />
          </div>
        </div>

        {/* SPHF vs Non-SPHF Donut */}
        <div className="bg-white flex flex-col h-full">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            SPHF vs Non-SPHF
          </h3>
          {panel(has.sphf,
          <div className="flex-1 flex items-center justify-center">
            <div className="w-full max-w-lg">
              <ReactApexChart
                options={{
                  chart: { type: "donut" },
                  labels: ["SPHF", "Non-SPHF"],
                  colors: [BRAND.teal, BRAND.gold],
                  legend: {
                    position: "bottom",
                    horizontalAlign: "center",
                    fontSize: "12px",
                  },
                  dataLabels: {
                    enabled: true,
                    style: { fontSize: "14px", fontWeight: "bold" },
                  },
                  plotOptions: {
                    pie: {
                      dataLabels: { minAngleToShowLabel: 18 },
                      donut: {
                        size: "65%",
                        labels: {
                          show: true,
                          value: { fontSize: "18px", fontWeight: 700, formatter: (val) => formatCountFit(val) },
                          total: {
                            show: true,
                            label: "Total",
                            formatter: (w) => formatCountFit(w.globals.seriesTotals.reduce((a, b) => a + b, 0)),
                            fontSize: "18px",
                            fontWeight: "bold",
                            color: "#374151",
                          },
                        },
                      },
                    },
                  },
                }}
                series={[data.registeredSPHF, data.registeredNonSPHF]}
                type="donut"
                height={300}
              />
            </div>
          </div>)}
        </div>
      </div>
      </>)}

      {/* Row 3: Marital Status, Family Size Distribution, Household Size Statistics */}
      {showRow(has.marital, has.familySize, has.hhSize) && (<>
      <hr className="border-t-2 border-dashed border-teal-700 first:hidden" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Marital Status */}
        <div className="bg-white flex flex-col h-full">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Marital Status
          </h3>
          {panel(has.marital,
          <div className="flex-1">
            <DashboardBarChart
              horizontal
              categories={maritalCategories}
              series={maritalSeries}
              unit="persons"
            />
          </div>)}
        </div>

        {/* Family Size Distribution (horizontal bars) */}
        <div className="bg-white flex flex-col h-full rounded-lg shadow-sm">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Family Size Distribution
          </h3>
          <div className="flex-1 p-2">
            <FamilySizePie familySizeData={familySizeData} />
          </div>
        </div>

        {/* Household Size Statistics */}
        <div className="bg-white flex flex-col h-full">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Household Size Statistics
          </h3>
          {panel(has.hhSize,
          <div className="flex-1 flex flex-col justify-center space-y-4 p-4">
            {/* Mean */}
            <div className="relative rounded-xl bg-[#137E81] p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-white/90">
                    Mean
                  </div>
                  <div className="text-3xl font-bold text-white">
                    {data.familyStats.mean.toFixed(1)}
                  </div>
                </div>
              </div>
            </div>
            {/* Median & Std Dev Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="relative rounded-xl bg-[#E6F2F2] border border-[#7FBFC1] p-4">
                <div className="text-xs font-semibold text-[#0B5557]">
                  Median
                </div>
                <div className="text-2xl font-bold text-[#137E81]">
                  {data.familyStats.median.toFixed(1)}
                </div>
              </div>
              <div className="relative rounded-xl bg-[#E6F2F2] border border-[#7FBFC1] p-4">
                <div className="text-xs font-semibold text-[#0B5557]">
                  Std Dev
                </div>
                <div className="text-2xl font-bold text-[#137E81]">
                  {data.familyStats.std.toFixed(2)}
                </div>
              </div>
            </div>
            {/* Min & Max Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="relative rounded-xl bg-[#E6F2F2] border border-[#7FBFC1] p-4">
                <div className="text-xs font-semibold text-[#0B5557]">
                  Min Size
                </div>
                <div className="text-2xl font-bold text-[#137E81]">
                  {data.familyStats.min.toFixed(0)}
                </div>
              </div>
              <div className="relative rounded-xl bg-[#E6F2F2] border border-[#7FBFC1] p-4">
                <div className="text-xs font-semibold text-[#0B5557]">
                  Max Size
                </div>
                <div className="text-2xl font-bold text-[#137E81]">
                  {data.familyStats.max.toFixed(0)}
                </div>
              </div>
            </div>
          </div>)}
        </div>
      </div>
      </>)}

      {/* Row 4: Approved Household Gender Statistics Table */}
      {showRow(has.genderTable) && (<>
      <hr className="border-t-2 border-dashed border-teal-700 first:hidden" />
      <div className="grid grid-cols-1 gap-4">
        <div>
          <h3 className="text-xl font-bold uppercase text-[#3A3A3A] mb-3 text-center text-balance">
            Approved Household Gender Statistics
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-white bg-[#137e81] font-medium">
                    District
                  </th>
                  <th className="px-4 py-3 text-center text-white bg-[#137e81] font-medium">
                    Total Approved Households
                  </th>
                  <th className="px-4 py-3 text-center text-white bg-[#137e81] font-medium">
                    Male Count
                  </th>
                  <th className="px-4 py-3 text-center text-white bg-[#137e81] font-medium">
                    Female Count
                  </th>
                  <th className="px-4 py-3 text-center text-white bg-[#137e81] font-medium">
                    Total Members
                  </th>
                  <th className="px-4 py-3 text-center text-white bg-[#137e81] font-medium">
                    Male Percentage
                  </th>
                  <th className="px-4 py-3 text-center text-white bg-[#137e81] font-medium">
                    Female Percentage
                  </th>
                </tr>
              </thead>
              <tbody>
                {districtWiseApprovedHousehold &&
                districtWiseApprovedHousehold.length > 0 ? (
                  districtWiseApprovedHousehold.map((district, index) => (
                    <tr
                      key={index}
                      className={`${
                        index % 2 === 0 ? "bg-[#F6F7F9]" : "bg-white"
                      } hover:bg-gray-100 transition-colors`}
                    >
                      <td className="px-4 py-3 text-gray-800 border-b font-medium">
                        {district.district_name || "N/A"}
                      </td>
                      <td className="px-4 py-3 text-center border-b font-semibold">
                        {formatCount(parseInt(district.total_approved_households) || 0)}
                      </td>
                      <td className="px-4 py-3 text-center border-b font-semibold">
                        {formatCount(parseInt(district.male_count) || 0)}
                      </td>
                      <td className="px-4 py-3 text-center border-b font-semibold">
                        {formatCount(parseInt(district.female_count) || 0)}
                      </td>
                      <td className="px-4 py-3 text-center border-b font-semibold">
                        {formatCount(parseInt(district.total_members) || 0)}
                      </td>
                      <td className="px-4 py-3 text-center border-b font-semibold">
                        {parseFloat(district.male_percentage || 0).toFixed(2)}%
                      </td>
                      <td className="px-4 py-3 text-center border-b font-semibold">
                        {parseFloat(district.female_percentage || 0).toFixed(2)}
                        %
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-4 py-8 text-center text-gray-500 border-b"
                    >
                      {loading
                        ? "Loading household data..."
                        : "No household data available"}
                    </td>
                  </tr>
                )}
                {grandTotals &&
                  districtWiseApprovedHousehold &&
                  districtWiseApprovedHousehold.length > 0 && (
                    <tr className="bg-[#137e81] text-white font-bold">
                      <td className="px-4 py-3 text-left border-b">TOTAL</td>
                      <td className="px-4 py-3 text-center border-b">
                        {grandTotals.totalApproved.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center border-b">
                        {grandTotals.maleCount.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center border-b">
                        {grandTotals.femaleCount.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center border-b">
                        {grandTotals.totalMembers.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center border-b">
                        {grandTotals.overallMalePct}%
                      </td>
                      <td className="px-4 py-3 text-center border-b">
                        {grandTotals.overallFemalePct}%
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      </>)}
    </div>
  );
};

export default InformationMembersTab;
